import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import OnboardingShell from "./components/OnboardingShell";
import { API_BASE_URL } from "./config";
import VerifiedPaymentReceiptModal from "./admin/VerifiedPaymentReceiptModal";

const ALL_ONBOARDING_KEYS = [
  "step1Data",
  "step2Data",
  "signature",
  "selectedProducts",
  "subscription",
  "goLiveDate",
];

function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [backendOrder, setBackendOrder] = useState(null);

  const status = (searchParams.get("status") || "").toLowerCase();
  const orderId = searchParams.get("order_id");
  const success = status === "success";

  useEffect(() => {
    if (orderId) {
      axios.get(`${API_BASE_URL}/api/payment/order/${orderId}`)
        .then((res) => {
          if (res.data) {
            setBackendOrder(res.data);
          }
        })
        .catch((err) => {
          console.warn("Could not fetch backend order data:", err);
        });
    }
  }, [orderId]);

  const getCustomerReceiptData = () => {
    const step1 = JSON.parse(localStorage.getItem("finalStep1Data") || localStorage.getItem("step1Data") || "{}");
    const step2 = JSON.parse(localStorage.getItem("finalStep2Data") || localStorage.getItem("step2Data") || "{}");
    const category = backendOrder?.productCategory || step2.category || localStorage.getItem("productCategory") || "Workflow AI";

    const selectedProducts = step2.selectedProducts || {};
    let totalAmount = 0;
    Object.keys(selectedProducts).forEach((key) => {
      const qty = Number(selectedProducts[key] || 0);
      if (key === "videosage") totalAmount += qty * 50;
      else if (key === "screensage") totalAmount += qty * 40;
      else if (key === "codesage") totalAmount += qty * 60;
      else if (key === "aibuddy_basic") totalAmount += qty * 4500;
      else if (key === "aibuddy_premium") totalAmount += qty * 6500;
      else if (key === "visionix_ai" || key === "face_recognition") totalAmount += qty * 1;
      else if (key.startsWith("visionix")) totalAmount += qty * 4500;
      else if (key === "people_connect_ai" || key.startsWith("peopleconnect")) totalAmount += qty * 5000;
      else if (key === "vrm_ai_estate") totalAmount += qty * 50;
      else if (key === "exit_intelligence") totalAmount += qty * 50;
    });

    if (backendOrder?.amount) {
      totalAmount = Number(backendOrder.amount);
    }

    const companyName = backendOrder?.companyName || step1.companyName || "";
    const purchaser = backendOrder?.purchaserName || step1.purchaserName || "";
    const email = backendOrder?.companyEmail || step1.companyEmail || "";
    const phone = backendOrder?.phone || step1.phone || "";
    const realTxnId = backendOrder?.trackingId || backendOrder?.orderId || orderId || "";

    return {
      company: companyName,
      companyName: companyName,
      purchaserName: purchaser,
      email: email,
      phone: phone,
      productCategory: category,
      product: backendOrder?.purchaseDetails || (category === "visionix_ai" ? "Visionix AI" : category === "workflow_ai" ? "Workflow AI" : category === "people_connect_ai" ? "People Connect AI" : category),
      plan: backendOrder?.plan || step2.subscription || "Monthly Plan",
      orderId: orderId || backendOrder?.orderId || "",
      transactionId: realTxnId,
      amount: totalAmount,
    };
  };

  useEffect(() => {
    if (success) {
      localStorage.setItem("finalStep1Data", localStorage.getItem("step1Data") || "{}");
      localStorage.setItem("finalStep2Data", localStorage.getItem("step2Data") || "{}");
      ALL_ONBOARDING_KEYS.forEach((key) => localStorage.removeItem(key));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewAgreement = async () => {
    try {
      setPreviewLoading(true);

      const step2 = JSON.parse(localStorage.getItem("finalStep2Data") || localStorage.getItem("step2Data") || "{}");
      const category = step2.category || localStorage.getItem("productCategory");

      // Check if Admin set a custom uploaded Agreement PDF for this product
      const adminProducts = JSON.parse(localStorage.getItem("vrm_admin_products") || "[]");
      const selectedAdminProduct = adminProducts.find(
        (p) => p.slug === category || 
               p.name.toLowerCase() === (category || "").toLowerCase() ||
               (p.category && p.category.toLowerCase() === (category || "").toLowerCase())
      );

      if (selectedAdminProduct && selectedAdminProduct.agreementPdf && selectedAdminProduct.agreementPdf.dataUrl) {
        const win = window.open();
        if (win) {
          win.document.write(`<html><head><title>${selectedAdminProduct.name} Agreement</title></head><body style="margin:0;"><iframe src="${selectedAdminProduct.agreementPdf.dataUrl}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100vh;" allowfullscreen></iframe></body></html>`);
        } else {
          window.open(selectedAdminProduct.agreementPdf.dataUrl, "_blank");
        }
        setPreviewLoading(false);
        return;
      }

      // Strategy 1: Try fetching directly from database via orderId
      if (orderId) {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/onboarding/agreement/order/${orderId}`,
            { responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          window.open(url, "_blank", "noopener,noreferrer");
          return;
        } catch (orderErr) {
          console.warn("Could not fetch agreement by orderId, falling back to local payload:", orderErr);
        }
      }

      // Strategy 2: Fallback using local storage data
      const step1 = JSON.parse(localStorage.getItem("finalStep1Data") || localStorage.getItem("step1Data") || "{}");

      const selectedProducts = step2.selectedProducts || {};
      let totalAmount = 0;
      let interviewCount = 0;
      let workflowAiDetails = [];
      let aiBuddyDetails = [];
      let workflowAiCredits = 0;

      Object.keys(selectedProducts).forEach((key) => {
        const qty = Number(selectedProducts[key] || 0);
        interviewCount += qty;
        if (key === "videosage") { totalAmount += qty * 50; workflowAiCredits += qty; workflowAiDetails.push(`VideoSage: ${qty}`); }
        else if (key === "screensage") { totalAmount += qty * 40; workflowAiCredits += qty; workflowAiDetails.push(`ScreenSage: ${qty}`); }
        else if (key === "codesage") { totalAmount += qty * 60; workflowAiCredits += qty; workflowAiDetails.push(`CodeSage: ${qty}`); }
        else if (key === "aibuddy_basic") { totalAmount += qty * 4500; aiBuddyDetails.push(`Basic: ${qty} Mo`); }
        else if (key === "aibuddy_premium") { totalAmount += qty * 6500; aiBuddyDetails.push(`Premium: ${qty} Mo`); }
        else if (key === "visionix_ai" || key === "face_recognition") { totalAmount += qty * 1; aiBuddyDetails.push(`Visionix AI: ${qty} Mo`); }
        else if (key.startsWith("visionix")) { totalAmount += qty * 4500; aiBuddyDetails.push(`${key}: ${qty}`); }
        else if (key === "people_connect_ai" || key.startsWith("peopleconnect")) { totalAmount += qty * 5000; aiBuddyDetails.push(`People Connect AI: ${qty}`); }
        else if (key === "vrm_ai_estate") { totalAmount += qty * 50; aiBuddyDetails.push(`VRM AI Estate: ${qty}`); }
        else if (key === "exit_intelligence") { totalAmount += qty * 50; aiBuddyDetails.push(`Exit Intelligence: ${qty}`); }
      });

      let breakdownString = "";
      if (workflowAiDetails.length > 0) {
        breakdownString += `Workflow AI [${workflowAiDetails.join(", ")} | Total Credits: ${workflowAiCredits}] `;
      }
      if (aiBuddyDetails.length > 0) {
        breakdownString += `[${aiBuddyDetails.join(", ")}]`;
      }

      const payload = {
        companyName: step1.companyName || "",
        address: step1.address || "",
        companyEmail: step1.companyEmail || "",
        phone: step1.phone || "",
        agreementSignature: step1.signature || step2.signature || "",
        productCategory: step2.category || "workflow_ai",
        purchaserName: step1.purchaserName || "",
        purchaserDesignation: step1.purchaserDesignation || "",
        expectedGoLiveDate: step2.goLiveDate || "",
        interviewCount: interviewCount,
        totalAmount: totalAmount,
        purchaseDetails: breakdownString.trim(),
      };

      const res = await axios.post(
        `${API_BASE_URL}/onboarding/agreement/preview`,
        payload,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      alert("Couldn't load the agreement preview. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  if (success) {
    return (
      <OnboardingShell step={5} title="">
        <div className="success-container">
          <div className="success-seal-wrap">
            <div className="success-seal-glow"></div>
            <div className="success-seal">
              <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
          </div>
          <h2 className="success-title">Payment Successful</h2>
          <p className="success-subtext">
            Order <strong>{orderId}</strong> was verified automatically by CCAvenue.<br />
            You'll receive your account credentials by email shortly.
          </p>
          <div className="success-actions" style={{ flexDirection: "column", gap: "16px", alignItems: "center" }}>
            <button
              className="onboard-button onboard-button--primary"
              type="button"
              onClick={handleViewAgreement}
              disabled={previewLoading}
              style={{ width: "100%", maxWidth: "260px" }}
            >
              {previewLoading ? "Preparing agreement…" : "View Agreement ↗"}
            </button>
            <button
              className="onboard-button secondary"
              type="button"
              onClick={() => setShowReceiptModal(true)}
              style={{ width: "100%", maxWidth: "260px" }}
            >
              Download Receipt
            </button>
            <button
              className="onboard-button secondary"
              type="button"
              onClick={() => {
                localStorage.clear();
                navigate("/");
              }}
              style={{ width: "100%", maxWidth: "260px", background: "transparent", border: "none", color: "#64748b", textDecoration: "underline", boxShadow: "none" }}
            >
              Start a new onboarding
            </button>
          </div>
        </div>

        <VerifiedPaymentReceiptModal
          isOpen={showReceiptModal}
          onClose={() => setShowReceiptModal(false)}
          data={getCustomerReceiptData()}
        />
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell step={3} title="">
      <div className="success-container">
        <h2 className="success-title" style={{ color: "#DC2626" }}>Payment Not Completed</h2>
        <p className="success-subtext">
          {status === "error"
            ? "Something went wrong while confirming your payment."
            : "Your payment was cancelled or declined."}
          {orderId ? <> Reference: <strong>{orderId}</strong></> : null}
          <br />
          No amount has been onboarded yet — you can safely try again.
        </p>
        <div className="button-row" style={{ justifyContent: "center" }}>
          <button
            className="onboard-button onboard-button--primary"
            type="button"
            onClick={() => navigate("/")}
          >
            Back to Payment
          </button>
        </div>
      </div>
    </OnboardingShell>
  );
}

export default PaymentStatus;
